import type { Props } from 'davaux/jsx-runtime'
import type { StyleObject, StyleProps, UiColor, UiRadius, UiSize } from './types.js'
import { cx, mergeStyle, pickStyleProps, radius } from './types.js'

const ROOT = 'dv-pagination'
const ITEM_CLS = 'dv-pagination-item'
const DOTS_CLS = 'dv-pagination-dots'

/** Props for the Pagination component. */
export interface PaginationProps extends StyleProps {
  /** Total number of pages; controls the range of page buttons rendered. */
  total: number
  /** Currently active (1-based) page number. */
  value?: number
  /** Number of page buttons shown on each side of the active page before dots appear. */
  siblings?: number
  /** Number of page buttons always visible at the start and end of the range. */
  boundaries?: number
  /** Returns the href for a given page number; when provided, items render as `<a>` tags instead of `<button>`. */
  getHref?: (page: number) => string
  /** Called with the target page number when a pagination item is clicked. Use instead of `getHref` for reactive/client-side navigation. */
  onClick?: (page: number) => void
  /** Controls the size of pagination items via a theme size token. */
  size?: UiSize
  /** Border radius of each pagination item. */
  radius?: UiRadius
  /** Accent color applied to the active page item. */
  color?: UiColor
  /** When true, renders first-page and last-page edge navigation buttons. */
  withEdges?: boolean
  /** When true, renders previous and next navigation buttons. */
  withControls?: boolean
  /** When true, disables all pagination items and prevents navigation. */
  disabled?: boolean
  [key: string]: unknown
}

function range(start: number, end: number): number[] {
  const result: number[] = []
  for (let i = start; i <= end; i++) result.push(i)
  return result
}

function buildPages(
  total: number,
  active: number,
  siblings: number,
  boundaries: number,
): (number | 'dots')[] {
  if (total <= 0) return []

  const totalPageNumbers = siblings * 2 + 3 + boundaries * 2
  if (totalPageNumbers >= total) {
    return range(1, total)
  }

  const leftSiblingIndex = Math.max(active - siblings, boundaries + 1)
  const rightSiblingIndex = Math.min(active + siblings, total - boundaries)

  const showLeftDots = leftSiblingIndex > boundaries + 2
  const showRightDots = rightSiblingIndex < total - boundaries - 1

  const leftBoundary = range(1, boundaries)
  const rightBoundary = range(total - boundaries + 1, total)

  if (!showLeftDots && showRightDots) {
    const leftItems = range(1, leftSiblingIndex + siblings + 1)
    return [...leftItems, 'dots', ...rightBoundary]
  }

  if (showLeftDots && !showRightDots) {
    const rightItems = range(rightSiblingIndex - siblings - 1, total)
    return [...leftBoundary, 'dots', ...rightItems]
  }

  const middleItems = range(leftSiblingIndex, rightSiblingIndex)
  return [...leftBoundary, 'dots', ...middleItems, 'dots', ...rightBoundary]
}

const PREV_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3L5 8l5 5"/></svg>'
const NEXT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3l5 5-5 5"/></svg>'
const FIRST_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 3L6 8l5 5M3 3v10"/></svg>'
const LAST_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3l5 5-5 5M13 3v10"/></svg>'
const DOTS_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor"><circle cx="3" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="13" cy="8" r="1.5"/></svg>'

function renderControl(
  href: string | undefined,
  onClick: (() => void) | undefined,
  content: string,
  disabled: boolean,
  active?: boolean,
): Promise<string> {
  if (href !== undefined) {
    return (
      <a
        class={ITEM_CLS}
        href={href}
        data-active={active || undefined}
        data-disabled={disabled || undefined}
        aria-current={active ? 'page' : undefined}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )
  }
  return (
    <button
      type='button'
      class={ITEM_CLS}
      data-active={active || undefined}
      data-disabled={disabled || undefined}
      disabled={disabled || undefined}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/** Renders an accessible `<nav>` pagination bar with page buttons, optional edge controls, and truncation dots. */
export function Pagination({
  total,
  value = 1,
  siblings = 1,
  boundaries = 1,
  getHref,
  onClick,
  size,
  radius: r,
  color,
  withEdges = false,
  withControls = true,
  disabled = false,
  class: className,
  style,
  ...rest
}: PaginationProps): Promise<string> {
  const cssVars: StyleObject = {}
  if (size !== undefined) cssVars['--dv-pagination-fz'] = `var(--dv-font-size-${size})`
  if (r !== undefined) cssVars['--dv-pagination-radius'] = radius(r) ?? ''
  if (color !== undefined) cssVars['--dv-pagination-color'] = `var(--dv-color-${color}-filled)`
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)

  const pages = buildPages(total, value, siblings, boundaries)

  const prevDisabled = disabled || value <= 1
  const nextDisabled = disabled || value >= total

  const items: Promise<string>[] = []

  if (withEdges) {
    items.push(
      renderControl(
        !prevDisabled && getHref ? getHref(1) : undefined,
        !prevDisabled && onClick ? () => onClick(1) : undefined,
        FIRST_SVG,
        prevDisabled,
      ),
    )
  }

  if (withControls) {
    items.push(
      renderControl(
        !prevDisabled && getHref ? getHref(value - 1) : undefined,
        !prevDisabled && onClick ? () => onClick(value - 1) : undefined,
        PREV_SVG,
        prevDisabled,
      ),
    )
  }

  for (const page of pages) {
    if (page === 'dots') {
      items.push(<span class={DOTS_CLS} dangerouslySetInnerHTML={{ __html: DOTS_SVG }} />)
    } else {
      const isActive = page === value
      items.push(
        renderControl(
          !disabled && getHref ? getHref(page) : undefined,
          !disabled && onClick ? () => onClick(page) : undefined,
          String(page),
          disabled,
          isActive,
        ),
      )
    }
  }

  if (withControls) {
    items.push(
      renderControl(
        !nextDisabled && getHref ? getHref(value + 1) : undefined,
        !nextDisabled && onClick ? () => onClick(value + 1) : undefined,
        NEXT_SVG,
        nextDisabled,
      ),
    )
  }

  if (withEdges) {
    items.push(
      renderControl(
        !nextDisabled && getHref ? getHref(total) : undefined,
        !nextDisabled && onClick ? () => onClick(total) : undefined,
        LAST_SVG,
        nextDisabled,
      ),
    )
  }

  return (
    <nav
      class={cx(ROOT, className)}
      style={mergeStyle({ ...cssVars, ...styleObj }, style)}
      aria-label='Pagination'
      {...ownRest}
    >
      {items}
    </nav>
  )
}
